import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import UpdateUTRModal from "./UpdateUTRModal";
import { getDisbursementDropdowns } from "../../../config.js";
import { resetSortingColumns } from "../../../utils/constant.js";
import { formatAmount } from "../../../utils/format.js";
import { axios_instance } from "../../../api/axios_instance";
import Table from "../../../layouts/Table";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import SingleSelectDropDown from "../../../layouts/SingleSelectDropDown";
import Header from "../../../layouts/Header";
import RefreshSpin from "../../../layouts/RefreshSpin";
import LoadingModal from "../../../layouts/LoadingModal.jsx";
import {
  decryptRequest,
  encryptRequest,
} from "../../../utils/encryptdecryptrequest.js";
import AlertBox from "../../../layouts/AlertBox.jsx";
import MultiSelectDropDownReset from "../../../layouts/MultiSelectDropdownReset";

const DashboardPayments = () => {
  const user = useSelector((state) => state.user);
  const initialFilterState = useMemo(
    () => ({
      searchType: "Loan ID",
      searchTypeInput: "",
      dateType: "",
      date_from: "",
      date_to: "",
      payeeTypes: [],
      holdStatus: "",
      cities: [],
      statuses: [],
      subStatuses: [],
      executives: [],
    }),
    []
  );

  const [filterValues, setFilterValues] = useState(initialFilterState);
  const [tabData, setTabData] = useState({
    caseData: [],
    counts: {
      tabCounts: [0, 0, 0, 0, 0, 0, 0],
      current_balance: 0,
    },
    total: 0,
  });

  const [sortingColumns, setSortingColumns] = useState({
    ...resetSortingColumns,
  });

  const [selectedSortingCol, setSelectedSortingCol] = useState();

  const initialDropDownData = useMemo(
    () => ({
      cities: [],
      statuses: [],
      subStatuses: [],
      executives: [],
      holdStatuses: [],
      dateTypeArr: [],
      searchTypeArr: [],
      payeeTypes: [],
    }),
    []
  );

  const [dropdownData, setDropdownData] = useState(initialDropDownData);
  const [alertState, setAlertState] = useState({
    showBox: false,
    message: "",
  });

  const [showUTRModal, setShowUTRModal] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showAdvanceFilter, setShowAdvanceFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [resetClicked, setResetClicked] = useState(false);
  const [showArrowTopButton, setShowArrowTopButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [currBalLoading, setCurrBalLoading] = useState(false);

  const dropdownContainer = useRef(null);
  const dropdownContainer2 = useRef(null);
  const dropdownContainer3 = useRef(null);
  const dropdownContainer4 = useRef(null);
  const dropdownContainer5 = useRef(null);

  const dropdownContainers = useMemo(
    () => [
      dropdownContainer,
      dropdownContainer2,
      dropdownContainer3,
      dropdownContainer4,
      dropdownContainer5,
    ],
    []
  );

  const fetchDisbursementCounts = async (isReset) => {
    setTabLoading(true);
    try {
      const encryptedData = encryptRequest({
        filterValues: isReset ? initialFilterState : filterValues,
        activeTab: isReset ? 0 : activeTab,
      });

      const response = await axios_instance({
        url: "/dts/getDisbursementCount",
        method: "POST",
        data: { reqData: encryptedData },
      });
      if (response.status === 200) {
        const { ...tabs } = decryptRequest(response.data.data);
        setTabData((prevState) => {
          const tabCounts = [...prevState.counts.tabCounts];

          for (const key in tabs) {
            tabCounts[parseInt(key)] = tabs[key];
          }
          const newState = {
            ...prevState,
            counts: { ...prevState.counts, tabCounts },
          };

          return newState;
        });
      }
    } catch (error) {
    } finally {
      setTabLoading(false);
    }
  };

  const fetchCurrentBalance = async (isReset = false) => {
    setCurrBalLoading(true);
    try {
      let response = await axios_instance({
        url: "/dts/getCurrentBalance",
        method: "POST",
        data: isReset ? initialFilterState : filterValues,
      });

      setTabData((prevState) => ({
        ...prevState,
        counts: {
          ...prevState.counts,
          current_balance: response.data.current_balance,
        },
      }));
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status !== 500) {
      }
    } finally {
      setCurrBalLoading(false);
    }
  };

  const fetchCaseData = async (resetPage = false, isReset) => {
    setIsLoading(true);

    try {
      const encryptedData = encryptRequest({
        activeTab: isReset ? 0 : activeTab,
        page: resetPage ? 1 : page,
        filterValues: isReset ? initialFilterState : filterValues,
        sortingColumns: selectedSortingCol,
      });

      let caseresponse = await axios_instance({
        url: "/dts/getDTSList",
        method: "POST",
        data: { reqData: encryptedData },
      });

      const { pageOfResults: caseData, total } = decryptRequest(
        caseresponse.data.resData
      );

      setTabData((prevState) => {
        const tabCounts = [...prevState.counts.tabCounts];
        tabCounts[activeTab] = total;

        const newState = {
          ...prevState,
          caseData,
          total,
          counts: { ...prevState.counts, tabCounts },
        };

        return newState;
      });
    } catch (err) {
      if (err.response && err.response.status !== 500) {
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropDownData = useCallback(async () => {
    try {
      const {
        cities,
        executives,
        status: statuses,
        subStatus: subStatuses,
        holdStatuses,
        dateTypeArr,
        searchTypeArr,
        payeeTypeList,
      } = await getDisbursementDropdowns();

      setDropdownData((prevState) => ({
        ...prevState,
        statuses,
        subStatuses,
        executives,
        cities,
        holdStatuses,
        dateTypeArr,
        searchTypeArr,
        payeeTypes: payeeTypeList,
      }));
    } catch (error) {
      if (error.response && error.response.status !== 500) {
        return;
      }
    }
  }, []);

  const closeAllDropdowns = useCallback(
    (target) => {
      dropdownContainers
        .filter((container) => container !== target)
        .forEach((container) => {
          const ul = container.current;
          if (ul && ul.classList.contains("visible")) {
            ul.classList.remove("visible");
            ul.style.display = "none";
          }
        });
    },
    [dropdownContainers]
  );

  const handleDocumentClick = useCallback(
    (e) => {
      // Check if the click was outside the dropdown container
      if (
        !dropdownContainers.some(
          (container) =>
            container.current && container.current.contains(e.target)
        )
      ) {
        closeAllDropdowns();
      }
    },
    [dropdownContainers, closeAllDropdowns]
  );

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    window.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleDocumentClick]);

  useEffect(() => {
    fetchCaseData();
  }, [activeTab, page, selectedSortingCol]);

  useEffect(() => {
    fetchDisbursementCounts();
    fetchCurrentBalance();
    fetchDropDownData();
  }, []);

  const handleDateChange = (value, filterName) => {
    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [filterName]: value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e, filterName) => {
    const discrepancyValues = [3, 4, 12, 14];
    const { name, checked, value } = e.target;

    let temp = null;

    if (name === "selectall") {
      if (checked)
        temp = dropdownData[filterName].map(({ value }) => parseInt(value));
      else temp = [];
    } else {
      temp = [...filterValues[filterName]];

      if (checked) {
        if (name === "Discrepancy Identified") {
          temp = [...new Set([...temp, ...discrepancyValues])];
        } else {
          temp.push(parseInt(value));
        }
      } else {
        if (name === "Discrepancy Identified") {
          temp = temp.filter((num) => ![3, 4, 12, 14].includes(num));
        } else {
          temp = temp.filter((elem) => elem !== parseInt(value));
        }
      }
    }

    setFilterValues((prevFilterValues) => ({
      ...prevFilterValues,
      [filterName]: temp,
    }));
  };

  const handleSorting = async (filterName) => {
    let col = sortingColumns;
    const order = [0, -1].includes(col[filterName]) ? 1 : -1;

    col = { ...resetSortingColumns };
    col[filterName] = order;
    setSortingColumns(col);

    setSelectedSortingCol({ [filterName]: order });
  };

  const handleScroll = () => {
    if (window.scrollY > 100) setShowArrowTopButton(true);
    else setShowArrowTopButton(false);
  };

  const toggleDropDown = (container, e) => {
    closeAllDropdowns(container);
    const ul = container.current;
    if (ul.classList.contains("visible")) {
      ul.classList.remove("visible");
      ul.style.display = "none";
    } else {
      ul.classList.add("visible");
      ul.style.display = "block";
    }
    e.stopPropagation();
  };

  const navSelected = (e, id) => {
    e.preventDefault();

    setActiveTab(id);
    if (id !== 0) {
      setSortingColumns({ ...resetSortingColumns, lead_id: -1 });
      setSelectedSortingCol({ lead_id: -1 });
    } else {
      setSortingColumns({ ...resetSortingColumns });
      setSelectedSortingCol();
    }
    setPage(1);
  };

  const pageSelected = (e, pageNo) => {
    e.preventDefault();
    setPage(pageNo);
  };

  const prevPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage - 1);
  };

  const nextPage = (e) => {
    e.preventDefault();
    setPage((prevPage) => prevPage + 1);
  };

  const closeUTRModal = () => {
    setShowUTRModal(false);
  };

  const toggleAdvanceFilterBox = (e) => {
    e.preventDefault();
    setShowAdvanceFilter((prevShowAdvanceFilter) => !prevShowAdvanceFilter);
  };

  useEffect(() => {
    if (resetClicked) {
      setFilterValues(initialFilterState);

      setSortingColumns({ ...resetSortingColumns });
      setActiveTab(0);

      setPage(1);
      setResetClicked(false);

      setSelectedSortingCol();

      fetchDisbursementCounts(true);
      fetchCurrentBalance(true);

      if (activeTab === 0) {
        fetchCaseData(true, true);
      }
    }
  }, [resetClicked]);

  const resetFunc = async (e) => {
    e.preventDefault();
    setResetClicked(true);
  };

  const exportData = async () => {
    const { date_from, date_to } = filterValues;
    const { statuses, subStatuses: substatuses } = dropdownData;
    if (date_to) {
      if (!date_from) {
        openAlertBox("Please select start date");
        return;
      }
      const start = new Date(date_to);
      const end = new Date(date_from);
      const diff = end - start;
      const days = Math.abs(diff / 1000 / 60 / 60 / 24);
      if (days > 6) {
        openAlertBox(
          "The date range should be limited to one week from the selected date."
        );
        return;
      }
    }
    setShowLoadingModal(true);
    try {
      const reqData = encryptRequest({
        activeTab: activeTab,
        filterValues: { date_from, date_to, statuses, substatuses },
        sortingColumns: sortingColumns,
      });
      const result = await axios_instance({
        url: "/dts/exportListData",
        method: "POST",
        data: { reqData },
      });
      if (result.status === 200) {
        const decryptedData = decryptRequest(result.data.resData);
        window.open(decryptedData.url);
      }
    } catch (error) {
    } finally {
      setShowLoadingModal(false);
    }
  };

  const openAlertBox = (message) => {
    setAlertState((prevState) => ({ ...prevState, showBox: true, message }));
  };

  return (
    <span>
      <UpdateUTRModal
        showUTRModal={showUTRModal}
        closeUTRModal={closeUTRModal}
      />

      {showLoadingModal && <LoadingModal />}
      {alertState.showBox && (
        <AlertBox
          onClick={() => {}}
          message={alertState.message}
          closeModal={() =>
            setAlertState((prevState) => ({ ...prevState, showBox: false }))
          }
        />
      )}
      <Header
        isNavigationModal={showNavigationModal}
        closeNavigationModal={() => setShowNavigationModal(false)}
        openNavigationModal={() => setShowNavigationModal(true)}
      ></Header>

      <div className="container-fluid">
        <div className="row">
          <div className="main_wrapper">
            <div className="overlay" id="navigation-overly"></div>

            <section className="right_wrapper">
              <div
                className="exportLoader"
                id="dtsExportLoader"
                style={{
                  display: "none",
                  zIndex: "1120",
                  position: "fixed",
                  width: "100%",
                  height: "100%",
                  background: "rgba(0,0,0,.50)",
                  top: "0",
                }}
              >
                <img
                  src={`${process.env.REACT_APP_STYLES_PATH}loader.gif`}
                  style={{
                    position: "absolute",
                    top: "40%",
                    left: "50%",
                    marginLeft: "-24px",
                  }}
                  alt=""
                />
              </div>
              <div className="panel-white">
                <input type="hidden" id="headerSearch" value="" />
                <input type="hidden" id="tab_status" value="-1" />
                <input type="hidden" id="hod_status" value="0" />
                <input type="hidden" id="role_id" value="6" />

                <div className="grayFiltr-bg brdr-FB clearfix">
                  <div className="col-md-12 ">
                    <h3 className="list_title clearfix">
                      <span className="pull-left">Disbursed Cases</span>
                      <div className="pull-right font-18">
                        Current Balance -
                        <span
                          id="spanCurrentBal"
                          className={`font-md mrg-L10 ${
                            tabData.counts.current_balance >= 0
                              ? "col-green"
                              : "col-red"
                          }`}
                        >
                          {currBalLoading ? (
                            <span className="user_heading_3">
                              <i className="fa fa-refresh fa-spin"></i>
                            </span>
                          ) : (
                            <>
                              <span className="fa fa-inr"></span>{" "}
                              {formatAmount(tabData.counts.current_balance)}
                            </>
                          )}
                        </span>
                      </div>
                    </h3>
                  </div>
                </div>
                <div className="filter_flyout">
                  <div className="filter_box fm-filter filter-section-bx" id="">
                    <div className="row pad-L10">
                      <form id="form-filter-dts">
                        <div className="col-lg-2 col-md-2 pad-LR">
                          <div className="col-lg-6 col-md-6 pad-LR">
                            <SingleSelectDropDown
                              dataArr={dropdownData.searchTypeArr}
                              handleChange={handleFilterChange}
                              name="searchType"
                              value={filterValues.searchType}
                            ></SingleSelectDropDown>
                          </div>

                          <div className="col-lg-6 col-md-7 pad-LR">
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control "
                                name="searchTypeInput"
                                id="reg_no"
                                value={filterValues.searchTypeInput}
                                title="Loan ID"
                                placeholder={filterValues.searchType}
                                onChange={handleFilterChange}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-1 col-md-3 col-sm-3 col-xs-12 pad-LR">
                          <SingleSelectDropDown
                            dataArr={dropdownData.dateTypeArr}
                            handleChange={handleFilterChange}
                            name="dateType"
                            value={filterValues.dateType}
                          ></SingleSelectDropDown>
                        </div>
                        <div className="col-lg-2 col-md-2 col-sm-3 col-xs-12 pad-LR">
                          <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                            <div
                              className="input-append date input-group input-sm"
                              id="dp2"
                            >
                              <DatePicker
                                selected={filterValues.date_from}
                                onChange={(date) =>
                                  handleDateChange(date, "date_from")
                                }
                                filterDate={(date) => date <= new Date()}
                                placeholderText="Date From"
                                className="span2 form-control add-on calender filter-tb date-bg_input"
                                dateFormat="dd-MM-yyyy"
                              />
                            </div>
                          </div>

                          <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 sm-text-box">
                            <div
                              className="input-append date input-group input-sm"
                              id="dp3"
                            >
                              <DatePicker
                                selected={filterValues.date_to}
                                onChange={(date) =>
                                  handleDateChange(date, "date_to")
                                }
                                filterDate={(date) => date <= new Date()}
                                placeholderText="Date To"
                                className="span2 form-control add-on calender filter-tb date-bg_input"
                                dateFormat="dd-MM-yyyy"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-2 col-md-2 col-sm-6 ms-drpdwn pad-LR">
                          <div className="row">
                            <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 pad-R5">
                              <MultiSelectDropDownReset
                                data={dropdownData.statuses}
                                handleCheckboxChange={handleCheckboxChange}
                                toggleDropDown={toggleDropDown}
                                dropdownContainer={dropdownContainer}
                                title="Status"
                                filterName="statuses"
                                selectedDropdownData={filterValues.statuses}
                              ></MultiSelectDropDownReset>
                            </div>

                            <div className="col-lg-6 col-md-6 col-sm-6 mrg-all-0 pad-L5">
                              <MultiSelectDropDownReset
                                data={dropdownData.subStatuses}
                                handleCheckboxChange={handleCheckboxChange}
                                toggleDropDown={toggleDropDown}
                                dropdownContainer={dropdownContainer2}
                                title="Substatus"
                                filterName="subStatuses"
                                selectedDropdownData={filterValues.subStatuses}
                              ></MultiSelectDropDownReset>
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-1 col-md-2 col-sm-6 ms-drpdwn pad-LR">
                          <MultiSelectDropDownReset
                            data={dropdownData.executives}
                            handleCheckboxChange={handleCheckboxChange}
                            toggleDropDown={toggleDropDown}
                            dropdownContainer={dropdownContainer3}
                            title="Executive Name"
                            filterName="executives"
                            selectedDropdownData={filterValues.executives}
                          ></MultiSelectDropDownReset>
                        </div>

                        {user &&
                        (user.role_id === 1 || parseInt(user.city_id) === 0) ? (
                          <div className="col-lg-1 col-md-6 col-sm-6 mrg-all-0 pad-L5 ms-drpdwn ">
                            <MultiSelectDropDownReset
                              data={dropdownData.cities}
                              handleCheckboxChange={handleCheckboxChange}
                              toggleDropDown={toggleDropDown}
                              dropdownContainer={dropdownContainer4}
                              title="City"
                              filterName="cities"
                              selectedDropdownData={filterValues.cities}
                            ></MultiSelectDropDownReset>
                          </div>
                        ) : (
                          <></>
                        )}

                        {user && user.role_id === 7 ? (
                          <div className="col-lg-1 col-md-1 col-sm-3 col-xs-12 mrg-all-0 pad-L5 ms-drpdwn ">
                            <MultiSelectDropDownReset
                              data={dropdownData.payeeTypes}
                              handleCheckboxChange={handleCheckboxChange}
                              toggleDropDown={toggleDropDown}
                              dropdownContainer={dropdownContainer5}
                              title="Payee Type"
                              filterName="payeeTypes"
                              selectedDropdownData={filterValues.payeeTypes}
                            ></MultiSelectDropDownReset>
                          </div>
                        ) : (
                          ""
                        )}

                        {showAdvanceFilter ? (
                          <div className="advanceFilterBox">
                            <div className="col-lg-1 col-md-1 col-sm-6 ms-drpdwn pad-LR">
                              <div className="row">
                                <div className="col-lg-12 col-md-12 col-sm-6 mrg-all-0">
                                  <SingleSelectDropDown
                                    dataArr={dropdownData.holdStatuses}
                                    handleChange={handleFilterChange}
                                    name="holdStatus"
                                    value={filterValues.holdStatus}
                                  ></SingleSelectDropDown>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          ""
                        )}

                        <div className="col-lg-2 col-md-2 col-sm-3 pad-LR searchContainer">
                          <div className="form-group">
                            <button
                              type="submit"
                              className="btn rc-btn_primary pad-L20 pad-R20 mar-R3 searchResults"
                              onClick={() => {
                                fetchCaseData(true);
                                fetchDisbursementCounts();
                                fetchCurrentBalance();
                              }}
                              disabled={isLoading}
                            >
                              {isLoading ? "Please wait.." : "Search"}
                            </button>
                            <button
                              type="button"
                              className="btn rc-btn_rset pad-L20 pad-R20"
                              onClick={(e) => {
                                resetFunc(e);
                              }}
                              disabled={isLoading}
                            >
                              Reset
                            </button>
                            <div className="clearfix advTxt-filter advance-filter-container">
                              <button
                                id="advSearchFilter"
                                onClick={(e) => toggleAdvanceFilterBox(e)}
                                className="advSearchFilter"
                              >
                                {!showAdvanceFilter ? (
                                  <span className="fa fa-plus-square-o"></span>
                                ) : (
                                  <span className="fa fa-minus-square-o"></span>
                                )}
                                Advance Search
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                <div className="container-fluid" id="list_data">
                  <div className="list-box mrg-T20 mrg-B20">
                    <div className="fixed-tabnavs filter_flyout dts_tab_navs">
                      <ul className="nav nav-tabs" role="tablist">
                        <li
                          data-input="0"
                          role="presentation"
                          className={`${activeTab === 0 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="My Cases"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 0);
                            }}
                          >
                            My Cases
                            <RefreshSpin
                              isLoading={
                                activeTab === 0 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[0]}
                            />
                          </a>
                        </li>
                        {user && user.role_id === 7 ? (
                          <li
                            data-input="7"
                            role="presentation"
                            className={`${activeTab === 7 ? "active" : ""}`}
                          >
                            <a
                              href="#"
                              aria-controls="Pool Cases"
                              role="tab"
                              style={{
                                cursor: !isLoading ? "pointer" : "not-allowed",
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isLoading) navSelected(e, 7);
                              }}
                            >
                              Pool Cases
                              <RefreshSpin
                                isLoading={
                                  activeTab === 7 ? isLoading : tabLoading
                                }
                                count={tabData.counts.tabCounts[7]}
                              />
                            </a>
                          </li>
                        ) : (
                          ""
                        )}
                        <li
                          data-input="1"
                          role="presentation"
                          className={`${activeTab === 1 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="Not Assigned Cases"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 1);
                            }}
                            disabled={tabLoading}
                          >
                            New Requests
                            <RefreshSpin
                              isLoading={
                                activeTab === 1 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[1]}
                            />
                          </a>
                        </li>

                        <li
                          data-input="2"
                          role="presentation"
                          className={`${activeTab === 2 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="Partial Payment Cases"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 2);
                            }}
                          >
                            Partial Payment
                            <RefreshSpin
                              isLoading={
                                activeTab === 2 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[2]}
                            />
                          </a>
                        </li>

                        <li
                          data-input="3"
                          role="presentation"
                          className={`${activeTab === 3 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="Pre Payment"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 3);
                            }}
                          >
                            Pre Payment
                            <RefreshSpin
                              isLoading={
                                activeTab === 3 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[3]}
                            />
                          </a>
                        </li>

                        <li
                          data-input="4"
                          role="presentation"
                          className={`${activeTab === 4 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="All Cases"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 4);
                            }}
                          >
                            All Cases
                            <RefreshSpin
                              isLoading={
                                activeTab === 4 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[4]}
                            />
                          </a>
                        </li>
                        <li
                          data-input="5"
                          role="presentation"
                          className={`${activeTab === 5 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="Payment Request Not Raised"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 5);
                            }}
                          >
                            Payment Request Not Raised
                            <RefreshSpin
                              isLoading={
                                activeTab === 5 ? isLoading : tabLoading
                              }
                              count={tabData.counts.tabCounts[5]}
                            />
                          </a>
                        </li>
                        <li
                          data-input="6"
                          role="presentation"
                          className={`${activeTab === 6 ? "active" : ""}`}
                        >
                          <a
                            href="#"
                            aria-controls="L0 Cases"
                            role="tab"
                            style={{
                              cursor: !isLoading ? "pointer" : "not-allowed",
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isLoading) navSelected(e, 6);
                            }}
                          >
                            L0 Cases
                            <RefreshSpin
                              isLoading={tabLoading}
                              count={tabData.counts.tabCounts[6]}
                            />
                          </a>
                        </li>
                        <div className="pull-right">
                          {user && !user.read_only_user ? (
                            <button
                              className="btn rc-btn_rset exprt_excl mar-R3"
                              id="bulkPopupBtn"
                              type="button"
                              onClick={(e) => {
                                setShowUTRModal(true);
                                e.stopPropagation();
                              }}
                            >
                              <span className="exprt_excl_icon">
                                <img
                                  src={`${process.env.REACT_APP_STYLES_PATH}excel-icon.png`}
                                  alt=""
                                />
                              </span>
                              Update UTR
                            </button>
                          ) : (
                            <></>
                          )}

                          {user &&
                            user.access_type &&
                            user.access_type.includes("list") && (
                              <button
                                className="btn rc-btn_rset exprt_excl"
                                id="export"
                                type="button"
                                onClick={exportData}
                              >
                                <span className="exprt_excl_icon">
                                  <img
                                    src={`${process.env.REACT_APP_STYLES_PATH}excel-icon.png`}
                                    alt=""
                                  />
                                </span>
                                Export
                              </button>
                            )}
                        </div>
                      </ul>
                    </div>
                    <div
                      className="tab-content ad-fx-cls"
                      style={{ marginTop: "0px" }}
                    >
                      {!isLoading ? (
                        <Table
                          activeTab={activeTab}
                          page={page}
                          tabData={tabData}
                          statuses={dropdownData.statuses}
                          subStatuses={dropdownData.subStatuses}
                          handleSorting={handleSorting}
                          sortingColumns={sortingColumns}
                          prevPage={prevPage}
                          pageSelected={pageSelected}
                          nextPage={nextPage}
                          fetchCaseData={fetchCaseData}
                          fetchDisbursementCounts={fetchDisbursementCounts}
                        />
                      ) : (
                        <div
                          id="dtsMycase_processing"
                          className="dataTables_processing"
                          style={{
                            textAlign: "center",
                          }}
                        >
                          <img
                            src={`${process.env.REACT_APP_STYLES_PATH}loader.gif`}
                            alt=""
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <div className="arrowTop">
        {showArrowTopButton ? (
          <div className="pageToup">
            <button
              id="go-top"
              onClick={() =>
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
              }
            >
              <span className="fa fa-arrow-up" aria-hidden="true"></span>
            </button>
          </div>
        ) : (
          ""
        )}
      </div>
    </span>
  );
};

export default DashboardPayments;
